import React, { useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { get, map, forEach, find, isEmpty, remove } from "lodash"

import { APIResponse } from "redux/apis/types"

import { Panel } from "./index"
import {
  BaseTable,
  HeatMapList,
  InfoTable,
  SeparateTable,
} from "components/molecules/table"
import { tableColumns, infoTableColumns } from "config/table_columns"
import { Labels } from "components/atoms/input/input"
import { MiniChartComponent } from "components/molecules/chart/miniChart"
import { changeSymbol, changeTerm } from "redux/ducks/Chart"
import MiddleChart from "components/molecules/chart/middleChart"
import uuid from "uuid"
import ClassNames from "classnames"
import { LEG_TYPE_SELECT, ROUTE_LIST } from "config/const"
import { BaseSelect } from "components/atoms/input/select"
import { checkIsSp, checkIsBrowser } from "utils/util"
import { NewsList } from "./newsList"
import APIs from "redux/apis"
import store from "redux/store"
import { pageLoad, pageLoadRegist } from "redux/ducks/PageLoad"
import { useLodingTrigger } from "components/actions/loading"

type DataItem = {
  data: APIResponse.Res
  setData: Function
  num?: number
  changeNum?: Function
}

type ComplexIndexPanelType = {
  industryItem: DataItem
  constituentsItem: DataItem
  detailItem: DataItem
  location: object
  chartID: {
    industry: string
    constituents: string
  }
  heatMapItem: {
    canvasId: string
    setInfoData: Function
  }
  labelItem: {
    label: number
    changeLabel: Function
  }
}

export const ComplexIndexPanel: React.FC<ComplexIndexPanelType> = props => {
  const {
    industryItem,
    constituentsItem,
    detailItem,
    location,
    chartID,
    heatMapItem,
    labelItem,
  } = props

  const dispatch = useDispatch()
  const isPhone = checkIsSp()
  const browser = checkIsBrowser()
  const loadResistId = useMemo(() => uuid(), [])
  const { loadingTrigger } = useLodingTrigger()

  const [activeNewsTab, changeNewsTab] = useState(0)
  const [newsEffect, setNewsEffect] = useState(false)
  const [
    constituentsChangeButtonFlag,
    setConstituentsChangeButtonFlag,
  ] = useState(false)

  const newsTabList = [{ id: 0, name: "業種" }, { id: 1, name: "構成銘柄" }]
  const industryDataList = get(industryItem.data, "ResultList", [])
  const constituentsList = get(constituentsItem.data, "ResultList", [])
  const industryDataCount = get(industryItem.data, "TotalCount")
  const constituentsDataCount = get(constituentsItem.data, "TotalCount")

  const miniChartId = useMemo(() => {
    const idList: Array<string> = []
    forEach(industryDataList, () => {
      idList.push(uuid())
    })
    return idList
  }, [industryDataList])

  const callSymbolDetail = async (data: object) => {
    const SecurityType = get(data, "SecurityType")
    const Exchange = get(data, "Exchange")
    const Symbol = get(data, "Symbol")
    return await APIs.SymbolDetail.SymbolDetail({
      SecurityType: SecurityType,
      Exchange: Exchange,
      Symbol: Symbol || "",
    })
  }

  //業種チャート切替
  useEffect(() => {
    if (isEmpty(industryDataList) || labelItem.label !== 0) return
    store.dispatch(pageLoadRegist(loadResistId, true))
    dispatch(changeSymbol(chartID.industry, industryDataList[industryItem.num]))
    constituentsItem.changeNum(0)
    store.dispatch(pageLoadRegist(loadResistId, false))
  }, [industryDataList, industryItem.num, labelItem.label])

  //構成銘柄チャート・情報切替
  useEffect(() => {
    if (
      isEmpty(constituentsList) ||
      labelItem.label !== 0 ||
      !isEmpty(get(location, "state"))
    )
      return
    store.dispatch(pageLoadRegist(loadResistId, true))
    dispatch(
      changeSymbol(chartID.constituents, constituentsList[constituentsItem.num])
    )
    const callDetail = async () => {
      const symbolData = await callSymbolDetail(
        constituentsList[constituentsItem.num]
      )
      if (get(symbolData, "Result") === 0) {
        detailItem.setData(get(symbolData, "Symbol"))
      }
      store.dispatch(pageLoadRegist(loadResistId, false))
    }
    callDetail()
  }, [constituentsList, labelItem.label])

  //構成銘柄のチャートの移動ボタンで発火
  useEffect(() => {
    if (isEmpty(constituentsList) || !constituentsChangeButtonFlag) return
    store.dispatch(pageLoadRegist(loadResistId, true))
    dispatch(
      changeSymbol(chartID.constituents, constituentsList[constituentsItem.num])
    )
    const callDetail = async () => {
      const symbolData = await callSymbolDetail(
        constituentsList[constituentsItem.num]
      )
      if (get(symbolData, "Result") === 0) {
        detailItem.setData(get(symbolData, "Symbol"))
      }
      store.dispatch(pageLoadRegist(loadResistId, false))
    }
    callDetail()
    setConstituentsChangeButtonFlag(false)
  }, [constituentsChangeButtonFlag])

  useEffect(() => {
    heatMapItem.setInfoData(industryDataList[0])
  }, [industryDataList])

  useEffect(() => {
    if (isEmpty(constituentsItem.data)) return
    if (activeNewsTab === 1) {
      changeNewsTab(0)
    } else {
      setNewsEffect(true)
    }
  }, [industryItem.num])

  useEffect(() => {
    if (isEmpty(constituentsItem.data) || activeNewsTab === 1) return
    changeNewsTab(1)
  }, [constituentsItem.num])

  const industryLabel = [
    {
      id: 0,
      name: "一覧",
      component: [{}],
    },
    {
      id: 1,
      name: "ミニチャート",
      component: (
        <ul className="chart-list-contents chart-list thin">
          {map(industryDataList, (row, key: number) => {
            return get(row, "ChartFlg") ? (
              <MiniChartComponent
                key={key}
                data={row}
                id={miniChartId[key]}
                lineType={true}
              />
            ) : null
          })}
        </ul>
      ),
    },
    {
      id: 2,
      name: "ヒートマップ",
      component: (
        <HeatMapList
          data={industryItem.data}
          onClick={async (data: object, i: number) => {
            store.dispatch(pageLoadRegist(loadResistId, true))
            dispatch(changeSymbol(heatMapItem.canvasId, data))
            const symbolData = await callSymbolDetail(data)
            if (get(symbolData, "Result") === 0) {
              heatMapItem.setInfoData(get(symbolData, "Symbol"))
            }
            constituentsItem.changeNum(i)
            store.dispatch(pageLoadRegist(loadResistId, false))
          }}
          removeSymbolcode={true}
        />
      ),
    },
  ]
  const changeChartLeg = (id: string) => {
    forEach(miniChartId, items => {
      dispatch(changeTerm(items, Number(id)))
    })
  }

  return labelItem.label !== industryLabel[0].id ? (
    <Panel
      title=""
      className={ClassNames({
        "panel-mini-chart": labelItem.label === industryLabel[1].id,
        "panel-heat-map": labelItem.label === industryLabel[2].id,
      })}
      // refresh={() =>
      //   labelItem.label === industryLabel[2].id && store.dispatch(pageLoad())
      // }
      filter={
        <div className="control-row filter-control-row sp">
          <Labels
            className="btn-segmented-control btn-thin liquid-control filter-control-item"
            labelList={
              isPhone
                ? remove(industryLabel, items => {
                    return items.id !== 1
                  })
                : industryLabel
            }
            onChange={(id: number) => {
              loadingTrigger.setLoading(true)
              labelItem.changeLabel(id)
              industryItem.changeNum(0)
              constituentsItem.changeNum(0)
            }}
            checked={labelItem.label}
          />
          {!isPhone && labelItem.label === industryLabel[1].id && (
            <BaseSelect option={LEG_TYPE_SELECT} onChange={changeChartLeg} />
          )}
        </div>
      }
    >
      {get(find(industryLabel, { id: labelItem.label }), "component")}
    </Panel>
  ) : (
    <>
      <div className="square-contents">
        <Panel
          title=""
          className="square-panel"
          // refresh={() => store.dispatch(pageLoad())}
          filter={
            <div className="control-row filter-control-row sp">
              <Labels
                className="btn-segmented-control btn-thin liquid-control filter-control-item"
                labelList={
                  isPhone
                    ? remove(industryLabel, items => {
                        return items.id !== 1
                      })
                    : industryLabel
                }
                onChange={(id: number) => {
                  loadingTrigger.setLoading(true)
                  labelItem.changeLabel(id)
                }}
                checked={labelItem.label}
              />
              {!isPhone && labelItem.label === industryLabel[1].id && (
                <BaseSelect
                  option={LEG_TYPE_SELECT}
                  onChange={changeChartLeg}
                />
              )}
            </div>
          }
        >
          <div className="industry-table">
            <div>
              <SeparateTable
                className="table-striped table-list-view"
                list={tableColumns.index.industry.industry}
                onClick={(row: object, i: number) => {
                  industryItem.changeNum(i)
                  constituentsItem.changeNum(0)
                  dispatch(changeSymbol(chartID.industry, row))
                }}
                scrollable={true}
                data={industryDataList}
                active={industryItem.num}
                scrollID="Industry"
              />
            </div>
          </div>
        </Panel>
        <Panel
          title=""
          className="square-panel chart not-header"
          nonePanelHeader={true}
        >
          <MiddleChart
            id={chartID.industry}
            numberOfStocks={constituentsDataCount}
            movingDataItem={{
              active: industryItem.num,
              changeData: industryItem.changeNum,
              total: industryDataCount,
            }}
          />
        </Panel>
        <NewsList
          route={ROUTE_LIST.DOMESTIC_INDUSTRY}
          symbol={
            activeNewsTab === newsTabList[1].id &&
            get(detailItem.data, "Symbol")
          }
          tabItem={{
            tabList: newsTabList,
            activeTab: activeNewsTab,
            changeTab: changeNewsTab,
          }}
          effect={newsEffect}
          setEffect={setNewsEffect}
        />
      </div>
      <div className="square-contents">
        <Panel title="" className="square-panel not-header">
          <div className="industry-table">
            <div>
              <SeparateTable
                className="table-list-view table-liquid-cell table-striped"
                list={tableColumns.index.industry.relate}
                data={constituentsList}
                onClick={async (row: object, i: number) => {
                  constituentsItem.changeNum(i)
                  store.dispatch(pageLoadRegist(loadResistId, true))
                  dispatch(changeSymbol(chartID.constituents, row))
                  const symbolData = await callSymbolDetail(row)
                  if (get(symbolData, "Result") === 0) {
                    detailItem.setData(get(symbolData, "Symbol"))
                  }
                  store.dispatch(pageLoadRegist(loadResistId, false))
                }}
                active={constituentsItem.num}
                scrollable={constituentsList.length > 7}
                scrollID="Constituents"
              />
            </div>
          </div>
        </Panel>
        <Panel
          title=""
          className="square-panel chart not-header"
          nonePanelHeader={true}
        >
          <MiddleChart
            id={chartID.constituents}
            movingDataItem={{
              active: constituentsItem.num,
              changeData: constituentsItem.changeNum,
              total: constituentsDataCount,
              setFlag: setConstituentsChangeButtonFlag,
            }}
          />
        </Panel>
        <Panel title="" className="square-panel info not-header">
          <div className="constituents-info">
            <div className="title-border">構成銘柄情報</div>
            <InfoTable
              list={infoTableColumns.indicative}
              data={detailItem.data}
              className="table-striped table-index-detail"
              wrapperCls="index-detail-table-row"
            />
          </div>
        </Panel>
      </div>
    </>
  )
}
